import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/base/ScrollAnimations";

interface ProgramItem {
  code: string;
  name: string;
  tagline: string;
  detail: string;
  outcomes: string[];
  route?: string;
}

interface WingProgramsProps {
  title: string;
  programs: ProgramItem[];
  icon: string;
  iconBg: string;
}

const routeMap: Record<string, string> = {
  PIIC: "/piic",
  PTIM: "/ptim",
  Training: "/training",
  Experts: "/experts",
  PIARC: "/piarc",
  IJTAI: "/ijtai",
  AIRCon: "/aircon",
  InnoReadiness: "/programs",
  Awards: "/programs",
  TIExpo: "https://www.textileinnovationexpo.com/",
  Concurrent: "/programs",
};

export default function WingPrograms({ title, programs, icon, iconBg }: WingProgramsProps) {
  return (
    <section className="bg-white py-16 md:py-24 lg:py-32">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-tie-teal/10 mb-4 md:mb-6">
                <span className="text-xs font-semibold text-tie-teal uppercase tracking-wider">
                  / Programmes
                </span>
              </div>
              <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-tie-text-primary leading-tight">
                {title}
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {programs.map((item, index) => {
              const route = routeMap[item.code];
              const external = route?.startsWith("http");

              const CardContent = (
                <div className="group h-full p-5 md:p-6 rounded-xl bg-tie-grey-light border border-tie-grey-dark hover:border-tie-teal/30 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start justify-between mb-3 md:mb-4">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-9 h-9 rounded-lg ${iconBg} flex items-center justify-center`}>
                        <i className={`${icon} text-white text-sm`} />
                      </div>
                      <span className="text-xs font-bold text-tie-teal uppercase tracking-wider">
                        {item.code}
                      </span>
                    </div>
                    {route && (
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-tie-grey-dark">
                        <i className={`${external ? "ri-external-link-line" : "ri-arrow-right-line"} text-tie-teal text-sm`} />
                      </div>
                    )}
                  </div>

                  <h3 className="text-base md:text-lg font-semibold text-tie-text-primary mb-1">
                    {item.name}
                  </h3>
                  <p className="text-xs md:text-sm text-tie-text-muted mb-3 md:mb-4">
                    {item.tagline}
                  </p>
                  <p className="text-sm text-tie-text-secondary leading-relaxed mb-4 md:mb-5">
                    {item.detail}
                  </p>

                  <div className="space-y-2">
                    {item.outcomes.map((outcome) => (
                      <div key={outcome} className="flex items-center gap-2.5 text-sm text-tie-text-secondary">
                        <span className="w-1.5 h-1.5 rounded-full bg-tie-teal shrink-0" />
                        {outcome}
                      </div>
                    ))}
                  </div>

                  {route && (
                    <div className="mt-4 pt-4 border-t border-tie-grey-dark">
                      <span className="inline-flex items-center text-sm font-medium text-tie-teal group-hover:text-tie-teal-dark transition-colors">
                        {external ? "Visit Website" : "Learn more"}
                        <i className={`${external ? "ri-external-link-line" : "ri-arrow-right-line"} ml-1.5 text-xs group-hover:translate-x-1 transition-transform`} />
                      </span>
                    </div>
                  )}
                </div>
              );

              return (
                <ScrollReveal key={item.code} delay={index * 100}>
                  {route ? (
                    external ? (
                      <a href={route} target="_blank" rel="noopener noreferrer" className="block cursor-pointer">
                        {CardContent}
                      </a>
                    ) : (
                      <Link to={route} className="block cursor-pointer">
                        {CardContent}
                      </Link>
                    )
                  ) : (
                    CardContent
                  )}
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}